import {IconButton} from '@wandb/components';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {useDebounceState} from '../../../util/hooks';
import {useStatsigGatePersonalSavedViews} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {
  getWorkspacePreferences,
  getWorkspaceSortOptions,
  setWorkspacePreference,
} from '../hooks/useWorkspacePreferences';
import {useWorkspaceViewItemsByOwner} from '../hooks/useWorkspaceViewItemsByOwner';
import {useViewBarContext} from '../NamedWorkspaceViewBarContext';
import type {SortOption} from '../types';
import {LegacyNamedWorkspaceMenu} from './LegacyNamedWorkspaceMenu';
import {NamedWorkspaceViewSection} from './NamedWorkspaceViewSection';

export const NamedWorkspaceMenu = () => {
  const {currentView} = useViewBarContext();
  const {isPersonalSavedViewsEnabled, isLoading} =
    useStatsigGatePersonalSavedViews(currentView?.project?.entityName);
  if (currentView?.project?.entityName == null || isLoading) {
    return null;
  }
  return isPersonalSavedViewsEnabled ? (
    <OwnershipNamedWorkspaceMenu />
  ) : (
    <LegacyNamedWorkspaceMenu />
  );
};

const OwnershipNamedWorkspaceMenu = () => {
  const {sharedViews, personalWorkspaces, viewer, currentView} =
    useViewBarContext();
  const projectId = currentView?.project?.id ?? '';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, debouncedSearch, setSearchQuery] = useDebounceState(
    '',
    300
  );

  const preferences = getWorkspacePreferences(projectId);
  const initialSortOptions = getWorkspaceSortOptions(preferences);
  const [ownedByViewerSortOption, setOwnedByViewerSortOption] =
    useState<SortOption>(initialSortOptions.ownedByViewer);
  const [ownedByOthersSortOption, setOwnedByOthersSortOption] =
    useState<SortOption>(initialSortOptions.ownedByOthers);

  const {ownedByViewer, ownedByOthers} = useWorkspaceViewItemsByOwner({
    personalWorkspaces,
    sharedViews,
    viewerId: viewer?.id,
    sortOptions: {
      ownedByViewer: ownedByViewerSortOption,
      ownedByOthers: ownedByOthersSortOption,
    },
    searchQuery: debouncedSearch,
  });
  const totalCount = ownedByViewer.totalCount + ownedByOthers.totalCount;
  const filteredCount = ownedByViewer.items.length + ownedByOthers.items.length;

  if (totalCount === 0) {
    // Hide the menu button when there are no views to display.
    return null;
  }

  const updateOwnedByViewerSortOption = (value: SortOption) => {
    setWorkspacePreference(projectId, 'ownedByViewerSortOption', value);
    setOwnedByViewerSortOption(value);
  };
  const updateOwnedByOthersSortOption = (value: SortOption) => {
    setWorkspacePreference(projectId, 'ownedByOthersSortOption', value);
    setOwnedByOthersSortOption(value);
  };

  return (
    <DropdownMenu.Root open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DropdownMenu.Trigger data-test="nw-menu-trigger" className="h-32">
        <IconButton
          icon="menu"
          variant="secondary"
          isActive={isMenuOpen}
          onPress={() => {
            setIsMenuOpen(!isMenuOpen);
          }}
          aria-label="Toggle named workspace menu"
          data-dd-action-name="Toggle named workspace menu"
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          data-test="nw-menu-content"
          className={twMerge(
            'z-[3]', // This is a hack that we really need to move away from
            'w-[20rem] max-w-[20rem]',
            'overflow-y-overlay md:translate-x-[68px]',
            'shadow-md shadow-oblivion/[0.16] dark:shadow-oblivion/[0.48]'
          )}
          align="end"
          sideOffset={12}
          onCloseAutoFocus={e => {
            e.preventDefault();
          }}>
          <div className="border-b border-wb-border p-8 dark:border-moon-800">
            <TextField
              value={searchQuery}
              onChange={setSearchQuery}
              onKeyDown={(_, e) => e.stopPropagation()}
              placeholder="Search workspaces"
              icon="search"
              size="medium"
              aria-label="Search workspaces"
              variant="ghost"
              extraActions={
                searchQuery ? (
                  <IconButton
                    variant="ghost"
                    icon="close"
                    size="sm"
                    mr={4}
                    aria-label="Clear search"
                    onPress={() => setSearchQuery('')}
                  />
                ) : undefined
              }
            />
          </div>
          <div
            className={twMerge(
              filteredCount > 1
                ? 'max-h-[calc(100vh-24rem)] custom-height-nw:max-h-[calc(100vh-15rem)]'
                : 'max-h-fit',
              'min-h-[3rem] overflow-auto'
            )}>
            <NamedWorkspaceViewSection
              title="My views"
              infoText="These workspace views were created by you. You control whether teammates can edit each view."
              items={ownedByViewer.items}
              totalCount={ownedByViewer.totalCount}
              isFiltered={Boolean(debouncedSearch)}
              testId="owned-view-label"
              sortOption={ownedByViewerSortOption}
              onSelectSortOption={updateOwnedByViewerSortOption}
            />
            {ownedByViewer.totalCount > 0 && ownedByOthers.totalCount > 0 && (
              <DropdownMenu.Separator />
            )}
            <NamedWorkspaceViewSection
              title="Other views"
              infoText="These workspace views were created by your teammates. Use them to follow your team's analysis and collaborate on views with team edit access."
              items={ownedByOthers.items}
              totalCount={ownedByOthers.totalCount}
              isFiltered={Boolean(debouncedSearch)}
              testId="team-view-label"
              sortOption={ownedByOthersSortOption}
              onSelectSortOption={updateOwnedByOthersSortOption}
            />
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
